<script>
    import { onMount } from "svelte";

    import {
        getInsuranceType,
        getValidBillingData,
        isObjectEmpty,
    } from "../../../../../Utils/utilFunctions.svelte";

    import {
        CustomTooltip,
        TableComponentContainer,
    } from "../../../../Common/index.svelte";

    import { DirectInsuranceType, opvStatusDVA } from "../../../../../Utils/utilEnums.svelte";

    import { cacheInsuranceFunds } from "../../../../../Utils/utilCache.svelte";

    import { insuranceFund } from "../../../../../stores";
    import RerunOpvIcon from "./RerunOpvIcon.svelte";

    export let row = {};
    export let fromHeader = false;

    export let onRerunOpvSuccess;

    $: if (fromHeader && row !== undefined) {
        syncDisplayStyle();
    }

    let bgColor = "",
        textColor = "",
        displayText = "",
        showCheckMark = false,
        isValidate = false,
        infoIconPath = "alertIcon_green",
        failedOPV = false,
        tooltipText = "";

    onMount(() => {
        syncDisplayStyle();
    });

    const syncDisplayStyle = async () => {
        const { billingData, patient, opvStatusMedicare, opvStatusHealthFund, opvStatusVeteran } =
            row;

        const tColor = {
            noNeed: "#4A4AFF",
            notCheck: "#6E6893",
            fail: "#D30000",
            success: "#007F00",
        };
        const bColor = {
            noNeed: "#E6E6F2",
            default: "#F2F0F9",
            fail: "#FFE0E0",
            success: "#CDFFCD",
        };
        const iconPath = {
            noNeed: "alertIcon_purple",
            default: "alertIcon_gray",
            fail: "alertIcon_orange",
            success: "alertIcon_green",
        };

        let insuranceType,
            privateHealthFundId = Number(0),
            privateHealthFundName = "";

        if (billingData && !!billingData.length) {
            const latestBillingData = getValidBillingData(billingData);
            insuranceType = latestBillingData.insuranceType;
            privateHealthFundId = latestBillingData.patientInsuranceId;
        } else if (!patient || isObjectEmpty(patient)) {
            const validStatus = [1, 2, 3];

            displayText = "Missing Insurance";

            if (
                !validStatus.includes(opvStatusMedicare) &&
                !validStatus.includes(opvStatusHealthFund)
            ) {
                bgColor = bColor.default;
                textColor = tColor.notCheck;
                infoIconPath = iconPath.noNeed;
                tooltipText = "Both Medicare and Health Fund are not checked";
                return;
            }

            showCheckMark = true;

            if (opvStatusMedicare == 1 && opvStatusHealthFund == 1) {
                bgColor = bColor.success;
                textColor = tColor.success;
                infoIconPath = iconPath.success;
                isValidate = true;
                tooltipText = "Both Medicare and Health Fund validate";
                return;
            }

            bgColor = bColor.fail;
            textColor = tColor.fail;
            infoIconPath = iconPath.fail;
            isValidate = false;
            tooltipText =
                opvStatusMedicare == 1
                    ? "Health Fund failed validation"
                    : opvStatusHealthFund == 1
                      ? "Medicare failed validation"
                      : "Both Medicare and Health Fund failed validation";
            return;
        } else {
            insuranceType = patient.insuranceType;
            privateHealthFundId = patient.insuranceId;
        }

        displayText = getInsuranceType(insuranceType, "shortTitle");

        if (insuranceType == DirectInsuranceType.Private) {
            if (!$insuranceFund?.length) await cacheInsuranceFunds();

            if (privateHealthFundId) {
                const targetFund = $insuranceFund.find(
                    (fund) => fund.id == privateHealthFundId,
                );

                if (targetFund) privateHealthFundName = targetFund.name;
            }
        }

        const noNeedCheckInsurances = [10, 15, 16, 17, 18, 25, 26];

        // Insurances NO NEED CHECK Validation
        if (noNeedCheckInsurances.includes(insuranceType)) {
            bgColor = bColor.noNeed;
            textColor = tColor.noNeed;
            infoIconPath = iconPath.noNeed;
            tooltipText = "Nothing to check";
            return;
        }

        // Private checks status of Medicare and Health Fund
        if (insuranceType == DirectInsuranceType.Private) {
            const successState =
                opvStatusMedicare == 1 && opvStatusHealthFund == 1;
            const failState = opvStatusMedicare > 1 || opvStatusHealthFund > 1;

            failedOPV = failState;

            const _getHealthFundName = () => {
                if (!privateHealthFundName) return "Health Fund";

                return `Health Fund (${privateHealthFundName})`;
            };

            bgColor = successState
                ? bColor.success
                : failState
                  ? bColor.fail
                  : bColor.default;
            textColor = successState
                ? tColor.success
                : failState
                  ? tColor.fail
                  : tColor.notCheck;
            showCheckMark = successState || failState;
            infoIconPath = successState
                ? iconPath.success
                : failState
                  ? iconPath.fail
                  : iconPath.default;
            isValidate = successState;
            tooltipText = successState
                ? `Medicare & ${_getHealthFundName()} validation success`
                : failState
                  ? opvStatusMedicare > 1
                      ? `Medicare validation fail, ${_getHealthFundName()} validation success`
                      : `Medicare validation success, ${_getHealthFundName()} validation fail`
                  : opvStatusMedicare == 1
                    ? `Medicare validation success, ${_getHealthFundName()} not checked`
                    : opvStatusHealthFund == 1
                      ? `Medicare not checked, ${_getHealthFundName()} validation success`
                      : `Medicare & ${_getHealthFundName()} not checked`;
            return;
        }

        if(insuranceType == DirectInsuranceType.DVA) {
             const successState =
                opvStatusVeteran == 1;
            const failState = opvStatusVeteran > 1;

            failedOPV = failState;

            bgColor = successState
                ? bColor.success
                : failState
                  ? bColor.fail
                  : bColor.default;
            textColor = successState
                ? tColor.success
                : failState
                  ? tColor.fail
                  : tColor.notCheck;
            showCheckMark = successState || failState;
            infoIconPath = successState
                ? iconPath.success
                : failState
                  ? iconPath.fail
                  : iconPath.default;
            isValidate = successState;
            tooltipText = successState
                ? `DVA validation success`
                : failState
                  ? 'DVA validation fail'
                  : `DVA not checked`;
            return;
        }

        // OPV Medicare NOT CHECKED
        if (!opvStatusMedicare || opvStatusMedicare == 0) {
            bgColor = bColor.default;
            textColor = tColor.notCheck;
            infoIconPath = iconPath.default;
            tooltipText = "Medicare not checked";
            return;
        }

        failedOPV = opvStatusMedicare != 1;

        bgColor = opvStatusMedicare == 1 ? bColor.success : bColor.fail;
        textColor = opvStatusMedicare == 1 ? tColor.success : tColor.fail;
        showCheckMark = true;
        infoIconPath =
            opvStatusMedicare == 1 ? iconPath.success : iconPath.fail;
        isValidate = opvStatusMedicare == 1;
        tooltipText =
            opvStatusMedicare == 1
                ? "Medicare validation success"
                : "Medicare validation failed";
    };

    // HELPER FUNCTIONS
</script>

<TableComponentContainer customContainerClass={fromHeader ? "" : `min-w-[140px]`}>
    <div class="flex flex-1 gap-x-2 w-fit items-center justify-start">
        <CustomTooltip
            contentClass="bg-[#fff] border-none flex-wrap text-left"
            contentStyle="box-shadow: 4px 4px 13.7px 1px rgba(0, 0, 0, 0.25);"
            zIndex="20"
        >
            
            <div
                class={`flex items-center gap-x-2 p-1 px-2 rounded ${fromHeader ? 'rounded' : 'rounded-full'} whitespace-nowrap`}
                style="background-color: {bgColor};"
                slot="tooltipTrigger"
            >
                <div
                    class="w-2 h-2 rounded rounded-full"
                    style="background-color: {textColor};"
                />

                <div style="color: {textColor}; padding: 2px 2px 0 0;">
                    {displayText}
                </div>
            </div>

            <p style="color: {textColor};">{tooltipText}</p>
        </CustomTooltip>

        {#if failedOPV && !fromHeader}
            <RerunOpvIcon encounter={row} onSuccess={onRerunOpvSuccess} />
        {/if}

    </div>
</TableComponentContainer>
